// Helper functions for localStorage
const STORAGE_KEY = 'novas_students_data';

const saveToStorage = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};

// Initial student data
const initialStudentData = {
  // Тестовые профили удалены
};

// Load data from localStorage or use initial data
let allStudentData = loadFromStorage() || initialStudentData;

// Удаляем тестовые профили из localStorage, если они там есть
if (allStudentData['ivan-ivanov'] || allStudentData['maria-sidorova']) {
  delete allStudentData['ivan-ivanov'];
  delete allStudentData['maria-sidorova'];
  saveToStorage(allStudentData);
}

// Update existing invite links to remove basename (Router handles it)
const updateInviteLinks = (data) => {
  let updated = false;
  Object.keys(data).forEach(key => {
    if (data[key].inviteLink && data[key].inviteLink.includes('/NOVAS-2.0/')) {
      const urlParts = data[key].inviteLink.split('/');
      const slug = urlParts[urlParts.length - 1];
      data[key].inviteLink = `${window.location.origin}/student-invite/${slug}`;
      updated = true;
    }
  });
  return updated;
};

// Update invite links if needed
if (updateInviteLinks(allStudentData)) {
  saveToStorage(allStudentData);
}

// Save initial data to localStorage if it doesn't exist
if (!loadFromStorage()) {
  saveToStorage(allStudentData);
}

// Export functions to manage data
export const getStudentData = () => {
  // Всегда читаем актуальные данные из localStorage
  const stored = loadFromStorage();
  if (stored) {
    allStudentData = stored;
  }
  return allStudentData;
};

export const saveStudentData = (data) => {
  allStudentData = data;
  saveToStorage(data);
};

export const addStudent = (slug, studentData) => {
  // Проверяем, есть ли уже ссылка у студента
  if (!studentData.inviteLink) {
    // Генерируем уникальную ссылку только если её нет
    const inviteLink = generateInviteLink(slug);
    studentData.inviteLink = inviteLink;
  }
  
  // Добавляем флаги только если их нет
  if (!studentData.isRegistered) {
    studentData.isRegistered = false;
  }
  if (!studentData.createdAt) {
    studentData.createdAt = new Date().toISOString();
  }
  
  allStudentData[slug] = studentData;
  saveToStorage(allStudentData);
  
  return studentData;
};

// Функция для генерации уникальной ссылки приглашения
const generateInviteLink = (studentSlug) => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const hash = Math.random().toString(36).substring(2, 20);
  const uniqueSlug = `${studentSlug}-${timestamp}-${randomString}-${hash}`;
  
  const baseUrl = window.location.origin;
  return `${baseUrl}/student-invite/${uniqueSlug}`;
};

// Функция для получения студента по ссылке приглашения
export const getStudentByInviteLink = (inviteSlug) => {
  console.log('getStudentByInviteLink called with:', inviteSlug);
  
  // Обновляем данные из localStorage перед поиском
  const freshData = loadFromStorage() || {};
  allStudentData = freshData;
  
  console.log('Fresh student data loaded:', allStudentData);
  
  // Сначала попробуем найти по точному совпадению slug
  if (allStudentData[inviteSlug]) {
    console.log('Found student by exact slug match:', inviteSlug);
    return { ...allStudentData[inviteSlug], originalSlug: inviteSlug };
  }
  
  // Если не найдено, ищем по inviteLink в данных
  const studentEntry = Object.entries(allStudentData).find(([slug, data]) => {
    if (data.inviteLink) {
      // Проверяем, содержит ли ссылка наш slug
      const urlParts = data.inviteLink.split('/');
      const linkSlug = urlParts[urlParts.length - 1];
      return linkSlug === inviteSlug;
    }
    return false;
  });
  
  if (studentEntry) {
    const [studentSlug, studentData] = studentEntry;
    console.log('Found student by inviteLink match:', studentSlug);
    return { ...studentData, originalSlug: studentSlug };
  }
  
  console.log('No student found for slug:', inviteSlug);
  return null;
};

// Функция для отметки студента как зарегистрированного
export const markStudentAsRegistered = (studentSlug, userData) => {
  console.log('markStudentAsRegistered called with:', { studentSlug, userData });
  console.log('All student data keys:', Object.keys(allStudentData));
  
  // Ищем студента по slug
  if (allStudentData[studentSlug]) {
    console.log('Found student by slug:', studentSlug);
    
    allStudentData[studentSlug].isRegistered = true;
    allStudentData[studentSlug].registeredAt = new Date().toISOString();
    allStudentData[studentSlug].userAccount = {
      id: userData.id,
      email: userData.email,
      phone: userData.phone || '',
      registeredAt: new Date().toISOString()
    };
    saveToStorage(allStudentData);
    console.log('Returning studentSlug for redirect:', studentSlug);
    return studentSlug; // Возвращаем slug для редиректа
  }
  
  console.log('No student found for slug:', studentSlug);
  return null;
};

export const updateStudent = (slug, studentData) => {
  allStudentData[slug] = studentData;
  saveToStorage(allStudentData);
};

export const deleteStudent = (slug) => {
  delete allStudentData[slug];
  saveToStorage(allStudentData);
};

// Lesson management functions
const LESSONS_STORAGE_KEY = 'novas_lessons_data';

export const saveLessonsToStorage = (lessons) => {
  try {
    // Очищаем большие баннеры (увеличили лимит до 2MB)
    const cleanedLessons = lessons.map(lesson => {
      const cleanedLesson = { ...lesson };
      
      // Очищаем большие баннеры (увеличили лимит до 2MB)
      if (lesson.bannerImage && lesson.bannerImage.length > 2 * 1024 * 1024) {
        cleanedLesson.bannerImage = null;
      }
      // Очищаем большие doc баннеры (увеличили лимит до 2MB)
      if (lesson.docBanners) {
        Object.keys(lesson.docBanners).forEach(key => {
          if (lesson.docBanners[key] && lesson.docBanners[key].length > 2 * 1024 * 1024) {
            cleanedLesson.docBanners[key] = null;
          }
        });
      }
      
      // Логируем editorContents для каждого урока
      if (lesson.editorContents) {
        console.log(`Lesson ${lesson.id} editorContents:`, lesson.editorContents);
        lesson.editorContents.forEach(({ id, content }) => {
          if (content.includes('<img')) {
            console.log(`Lesson ${lesson.id} - Editor ${id} has images:`, content.match(/<img[^>]+>/g));
          }
          if (content.includes('<audio')) {
            console.log(`Lesson ${lesson.id} - Editor ${id} has audio:`, content.match(/<audio[^>]+>/g));
          }
        });
      }
      
      return cleanedLesson;
    });
    
    const lessonsJson = JSON.stringify(cleanedLessons);
    console.log('Saving lessons to localStorage, total size:', lessonsJson.length, 'characters');
    
    localStorage.setItem(LESSONS_STORAGE_KEY, lessonsJson);
    console.log('Lessons saved successfully to localStorage');
  } catch (error) {
    console.error('Error saving lessons to localStorage:', error);
    
    if (error.name === 'QuotaExceededError') {
      console.warn('Storage quota exceeded, but keeping existing data intact');
    }
  }
};

const loadLessonsFromStorage = () => {
  try {
    const stored = localStorage.getItem(LESSONS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading lessons from localStorage:', error);
    return [];
  }
};

export const getLessonsForStudent = (studentId) => {
  console.log('=== getLessonsForStudent DEBUG ===');
  console.log('studentId:', studentId);
  
  // Получаем уроки из общего расписания
  const allLessons = loadLessonsFromStorage();
  console.log('All lessons from storage:', allLessons);
  
  // Приводим studentId к строке для сравнения
  const studentIdStr = String(studentId);
  const scheduleLessons = allLessons.filter(lesson => String(lesson.studentId) === studentIdStr);
  console.log('Schedule lessons for student:', scheduleLessons);
  
  // Получаем уроки из профиля студента
  const studentData = allStudentData[studentId];
  console.log('Student data:', studentData);
  const profileLessons = studentData?.lessons || [];
  console.log('Profile lessons:', profileLessons);
  
  // Объединяем уроки из расписания и профиля
  const combinedLessons = [...scheduleLessons, ...profileLessons];
  console.log('Combined lessons:', combinedLessons);
  
  // Удаляем дубликаты по ID, но приоритет отдаем урокам из основного хранилища
  const uniqueLessons = [];
  const seenIds = new Set();
  
  // Сначала добавляем уроки из основного хранилища (они содержат полный контент)
  scheduleLessons.forEach(lesson => {
    if (!seenIds.has(lesson.id)) {
      uniqueLessons.push(lesson);
      seenIds.add(lesson.id);
    }
  });
  
  // Затем добавляем уроки из профиля, если их нет в основном хранилище
  profileLessons.forEach(lesson => {
    if (!seenIds.has(lesson.id)) {
      uniqueLessons.push(lesson);
      seenIds.add(lesson.id);
    }
  });
  
  console.log('Final unique lessons:', uniqueLessons);
  console.log('Lessons with content check:', uniqueLessons.map(lesson => ({
    id: lesson.id,
    title: lesson.title,
    hasContainers: !!lesson.containers,
    hasWhiteboards: !!lesson.whiteboards,
    hasEditorContents: !!lesson.editorContents,
    hasWhiteboardContents: !!lesson.whiteboardContents,
    source: scheduleLessons.find(l => l.id === lesson.id) ? 'main_storage' : 'profile'
  })));
  
  return uniqueLessons;
};

export const getLessonsForGroup = (groupId) => {
  console.log('=== getLessonsForGroup DEBUG ===');
  console.log('groupId:', groupId);
  
  // Получаем уроки из общего расписания
  const allLessons = loadLessonsFromStorage();
  console.log('All lessons from storage:', allLessons);
  
  // Приводим groupId к строке для сравнения
  const groupIdStr = String(groupId);
  const scheduleLessons = allLessons.filter(lesson => String(lesson.groupId) === groupIdStr);
  console.log('Schedule lessons for group:', scheduleLessons);
  
  // Получаем уроки из профиля группы
  const groupData = loadGroupsFromStorage()[groupId];
  console.log('Group data:', groupData);
  const profileLessons = groupData?.lessons || [];
  console.log('Profile lessons:', profileLessons);
  
  // Объединяем уроки из расписания и профиля
  const combinedLessons = [...scheduleLessons, ...profileLessons];
  console.log('Combined lessons:', combinedLessons);
  
  // Удаляем дубликаты по ID, но приоритет отдаем урокам из основного хранилища
  const uniqueLessons = [];
  const seenIds = new Set();
  
  // Сначала добавляем уроки из основного хранилища (они содержат полный контент)
  scheduleLessons.forEach(lesson => {
    if (!seenIds.has(lesson.id)) {
      uniqueLessons.push(lesson);
      seenIds.add(lesson.id);
    }
  });
  
  // Затем добавляем уроки из профиля, если их нет в основном хранилище
  profileLessons.forEach(lesson => {
    if (!seenIds.has(lesson.id)) {
      uniqueLessons.push(lesson);
      seenIds.add(lesson.id);
    }
  });
  
  console.log('Final unique lessons:', uniqueLessons);
  console.log('Lessons with content check:', uniqueLessons.map(lesson => ({
    id: lesson.id,
    title: lesson.title,
    hasContainers: !!lesson.containers,
    hasWhiteboards: !!lesson.whiteboards,
    hasEditorContents: !!lesson.editorContents,
    hasWhiteboardContents: !!lesson.whiteboardContents,
    source: scheduleLessons.find(l => l.id === lesson.id) ? 'main_storage' : 'profile'
  })));
  
  return uniqueLessons;
};

export const saveLesson = (lessonData) => {
  const allLessons = loadLessonsFromStorage();
  const lessonId = lessonData.id || Date.now().toString();
  
  console.log('=== saveLesson DEBUG ===');
  console.log('lessonData:', lessonData);
  console.log('bannerImage present:', !!lessonData.bannerImage);
  console.log('bannerImage type:', typeof lessonData.bannerImage);
  console.log('bannerImage length:', lessonData.bannerImage ? lessonData.bannerImage.length : 0);
  
  // Убеждаемся, что studentId сохраняется как строка
  const lessonWithId = { 
    ...lessonData, 
    id: lessonId,
    studentId: String(lessonData.studentId)
  };
  
  console.log('lessonWithId bannerImage present:', !!lessonWithId.bannerImage);
  
  // Если урок уже существует, обновляем его
  const existingIndex = allLessons.findIndex(lesson => lesson.id === lessonId);
  if (existingIndex !== -1) {
    allLessons[existingIndex] = lessonWithId;
    console.log('Updated existing lesson');
  } else {
    allLessons.push(lessonWithId);
    console.log('Added new lesson');
  }
  
  saveLessonsToStorage(allLessons);
  console.log('Lesson saved successfully');
  return lessonWithId;
};

export const getLessonById = (lessonId) => {
  const allLessons = loadLessonsFromStorage();
  const lesson = allLessons.find(lesson => lesson.id === lessonId);
  
  console.log('=== getLessonById DEBUG ===');
  console.log('lessonId:', lessonId);
  console.log('Found lesson:', lesson);
  console.log('Lesson bannerImage present:', !!lesson?.bannerImage);
  console.log('Lesson bannerImage type:', typeof lesson?.bannerImage);
  console.log('Lesson bannerImage length:', lesson?.bannerImage ? lesson.bannerImage.length : 0);
  
  return lesson;
};

// Export the data for backward compatibility
export { allStudentData };


// Group data functions
const GROUPS_STORAGE_KEY = 'novas_groups_data';

// ===== Lesson Library (templates/global/personal) =====
const LIBRARY_STORAGE_KEY = 'novas_library_lessons';

const loadLibraryFromStorage = () => {
  try {
    const stored = localStorage.getItem(LIBRARY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Error loading library lessons:', e);
    return [];
  }
};

const saveLibraryToStorage = (lessons) => {
  try {
    localStorage.setItem(LIBRARY_STORAGE_KEY, JSON.stringify(lessons || []));
    // notify UI about update
    try { window.dispatchEvent(new Event('libraryUpdated')); } catch (_) {}
  } catch (e) {
    console.error('Error saving library lessons:', e);
  }
};

export const upsertLibraryLesson = (lesson) => {
  const lessons = loadLibraryFromStorage();
  const idx = lessons.findIndex(l => l.id === lesson.id);
  const now = new Date().toISOString();
  const withMeta = { ...lesson, updatedAt: now, createdAt: lesson.createdAt || now };
  if (idx >= 0) lessons[idx] = withMeta; else lessons.push(withMeta);
  saveLibraryToStorage(lessons);
  return withMeta;
};

export const getLibraryLessons = () => loadLibraryFromStorage();

export const searchLibraryLessons = ({ q, topics, cefr, medium, price, sort, authorFilter } = {}) => {
  let res = loadLibraryFromStorage();
  if (authorFilter) res = res.filter(l => String(l.authorId || '') === String(authorFilter));
  if (q && q.trim()) {
    const s = q.toLowerCase();
    res = res.filter(l => (l.title || '').toLowerCase().includes(s) || (l.description || '').toLowerCase().includes(s));
  }
  if (Array.isArray(topics) && topics.length) {
    res = res.filter(l => (l.topics || []).some(t => topics.includes(t)));
  }
  if (Array.isArray(cefr) && cefr.length) {
    res = res.filter(l => (l.cefrLevels || []).some(c => cefr.includes(c)));
  }
  if (medium) {
    const m = Array.isArray(medium) ? medium : [medium];
    res = res.filter(l => m.includes(l.medium));
  }
  if (price) {
    if (price === 'free') res = res.filter(l => (l.price?.type || 'free') === 'free');
    if (price === 'paid') res = res.filter(l => (l.price?.type || 'free') === 'paid');
  }
  if (sort === 'popular') res = res.sort((a,b) => (b.title?.length||0) - (a.title?.length||0));
  return res;
};

export const getPersonalLessons = ({ teacherId } = {}) => {
  const all = loadLibraryFromStorage();
  if (!teacherId) return [];
  return all.filter(l => String(l.authorId || '') === String(teacherId) || (Array.isArray(l.purchasedBy) && l.purchasedBy.includes(String(teacherId))));
};

export const markLessonPurchased = (lessonId, teacherId) => {
  const all = loadLibraryFromStorage();
  const idx = all.findIndex(l => l.id === lessonId);
  if (idx < 0) return false;
  const item = all[idx];
  const set = new Set(item.purchasedBy || []);
  set.add(String(teacherId));
  all[idx] = { ...item, purchasedBy: Array.from(set) };
  saveLibraryToStorage(all);
  return true;
};

export const duplicateLessonForStudent = ({ originalId, studentId, teacherId }) => {
  const all = loadLibraryFromStorage();
  const orig = all.find(l => l.id === originalId);
  if (!orig) return null;
  const clone = {
    ...orig,
    id: `${originalId}__${studentId}__${Date.now()}`,
    originalId,
    studentId: String(studentId),
    authorId: String(teacherId),
    visibility: 'private',
    isTemplate: true,
    annotations: undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  all.push(clone);
  saveLibraryToStorage(all);
  return clone;
};

// One-time seeding helper to migrate mock lessons into the library for demo/personal view
export const seedLibraryIfEmpty = (mockLessons = [], authorId = 'teacher-1') => {
  const existing = loadLibraryFromStorage();
  if (Array.isArray(existing) && existing.length > 0) return false;
  const mapMedium = (tags = []) => {
    if (tags.includes('Video')) return 'video';
    if (tags.includes('Audio')) return 'audio';
    if (tags.includes('Text')) return 'text';
    return 'text';
  };
  const mapCEFR = (tags = []) => {
    if (tags.includes('Beginner')) return ['A2'];
    if (tags.includes('Advanced')) return ['C1'];
    if (tags.includes('Intermediate')) return ['B1'];
    return ['B1'];
  };
  const topicsFromTags = (tags = []) => tags.filter(t => !['Beginner','Intermediate','Advanced','Video','Text','Audio'].includes(t));
  const now = new Date().toISOString();
  const seeded = (mockLessons || []).map((m) => ({
    id: String(m.id ?? `seed-${Math.random().toString(36).slice(2)}`),
    title: m.title || 'Untitled',
    description: m.description || '',
    cover: m.imageUrl || '',
    topics: topicsFromTags(m.tags || []),
    cefrLevels: mapCEFR(m.tags || []),
    medium: mapMedium(m.tags || []),
    price: { type: 'free' },
    visibility: 'public',
    isTemplate: true,
    authorId: String(authorId),
    originalId: undefined,
    purchasedBy: [],
    createdAt: now,
    updatedAt: now,
  }));
  saveLibraryToStorage(seeded);
  return true;
};

// Reassign mistakenly authored public templates (seeded as teacher) back to 'system'
export const reassignSeededPublicTemplatesToSystem = (teacherId) => {
  try {
    const all = loadLibraryFromStorage();
    let changed = false;
    const updated = all.map(l => {
      if (
        String(l.authorId || '') === String(teacherId) &&
        l.isTemplate && (l.visibility || 'public') === 'public' &&
        (!Array.isArray(l.purchasedBy) || l.purchasedBy.length === 0)
      ) {
        changed = true;
        return { ...l, authorId: 'system' };
      }
      return l;
    });
    if (changed) saveLibraryToStorage(updated);
    return changed;
  } catch (_) {
    return false;
  }
};

// Migrate existing student lessons (novas_lessons_data) authored by teacher into personal library templates
// Только уроки с контентом (не из расписания)
export const migrateStudentInstancesToLibrary = (teacherId) => {
  try {
    const allLessons = loadLessonsFromStorage();
    const library = loadLibraryFromStorage();
    const seen = new Set(library.map(l => l.id));
    let changed = false;
    const toTemplate = (l) => ({
      id: `tmpl-${l.id}`,
      title: l.title || 'Untitled',
      description: '',
      cover: l.bannerImage || '',
      topics: [],
      cefrLevels: ['B1'],
      medium: 'text',
      price: { type: 'free' },
      visibility: 'private',
      isTemplate: true,
      authorId: String(teacherId),
      originalId: undefined,
      purchasedBy: [],
      createdAt: l.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    const additions = [];
    for (const l of allLessons) {
      // Проверяем, есть ли у урока контент (не из расписания)
      const hasContent = l.containers?.length > 0 || 
                        l.docContainers?.length > 0 ||
                        l.homeworkContainers?.length > 0 ||
                        l.whiteboards?.length > 0 ||
                        l.editorContents?.length > 0 ||
                        l.whiteboardContents?.length > 0 ||
                        l.bannerImage;
      
      // Мигрируем только уроки с контентом
      if (!seen.has(`tmpl-${l.id}`) && String(l.studentId || '') && l.title && hasContent) {
        additions.push(toTemplate(l));
      }
    }
    if (additions.length) {
      saveLibraryToStorage([...library, ...additions]);
      changed = true;
    }
    return changed;
  } catch (_) {
    return false;
  }
};

export const saveGroupsToStorage = (groups) => {
  try {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
  } catch (error) {
    console.error('Error saving groups to localStorage:', error);
  }
};

const loadGroupsFromStorage = () => {
  try {
    const stored = localStorage.getItem(GROUPS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error loading groups from localStorage:', error);
    return {};
  }
};

export const getGroupsData = () => {
  return loadGroupsFromStorage();
};

export const saveGroupData = (groupData) => {
  const groups = loadGroupsFromStorage();
  groups[groupData.id] = groupData;
  saveGroupsToStorage(groups);
};

export const getGroupData = (groupId) => {
  const groups = loadGroupsFromStorage();
  return groups[groupId] || null;
};

export const deleteGroup = (groupId) => {
  const groups = loadGroupsFromStorage();
  delete groups[groupId];
  saveGroupsToStorage(groups);
};


